import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@heroui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { KNOWLEDGE_BASE_KEYS } from 'hooks/useKnowledgeBase'
import { FileText, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'

export default function RawTextModal({ open, setOpen, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const queryClient = useQueryClient()

  const resetModal = () => {
    setFormData({ name: '', description: '', content: '' })
    setIsSubmitting(false)
    setOpen(false)
  }

  // Create text knowledge base mutation
  const { mutate: createTextKB } = useMutation({
    mutationFn: (data) => {
      return $fetch('/v1/knowledge/learn', {
        method: 'POST',
        body: data,
      })
    },
    onSuccess: () => {
      toast.success('Text knowledge base created successfully!')

      // Invalidate knowledge base queries to show new KB immediately
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_KEYS.lists() })

      onSuccess()
      resetModal()
    },
    onError: (error) => {
      console.error('Text KB creation error:', error)
      const errorMessage = error.data?.errors?.[0]?.message || 'Failed to create text knowledge base'
      toast.error(errorMessage)
      setIsSubmitting(false)
    },
  })

  const handleSubmit = async () => {
    if (!formData.content.trim()) {
      toast.error('Please enter some text content')
      return
    }

    if (!formData.name.trim()) {
      toast.error('Please enter a name for your knowledge base')
      return
    }

    setIsSubmitting(true)

    // Create the text knowledge base
    createTextKB({
      type: 'text',
      name: formData.name,
      description: formData.description || undefined,
      text: formData.content,
    })
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <Modal
      isOpen={open}
      onClose={resetModal}
      size="3xl"
      scrollBehavior="inside"
      radius="none"
      classNames={{
        base: 'max-h-[95vh]',
        body: 'p-0',
        wrapper: 'items-start mt-8',
      }}
    >
      <ModalContent radius="none">
        <ModalHeader className="border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold">Add Raw Text Content</h2>
              <p className="mt-1 text-sm text-gray-600">
                Create a knowledge base from text content you paste or type directly.
              </p>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="p-6">
          <div className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium">
                Knowledge Base Name
                <span className="ml-1 text-red-500">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                placeholder="e.g., Company Policies, Product Information"
                radius="none"
                classNames={{
                  input: 'bg-gray-50',
                  inputWrapper: 'border-gray-200',
                }}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Description
                <span className="ml-1 text-gray-400">(Optional)</span>
              </label>
              <Textarea
                value={formData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                placeholder="Describe what this content is about..."
                minRows={2}
                radius="none"
                classNames={{
                  input: 'bg-gray-50',
                  inputWrapper: 'border-gray-200',
                }}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Text Content
                <span className="ml-1 text-red-500">*</span>
              </label>
              <Textarea
                value={formData.content}
                onChange={e => handleInputChange('content', e.target.value)}
                placeholder="Paste or type your content here..."
                minRows={12}
                radius="none"
                classNames={{
                  input: 'font-mono text-sm',
                  inputWrapper: 'border-gray-200',
                }}
              />
              <p className="mt-2 text-xs text-gray-500">
                You can paste any text content here - policies, FAQs, documentation, or any other text-based information.
              </p>
            </div>

            {/* Content Preview */}
            {formData.content && (
              <div className="border border-gray-200 bg-white p-4">
                <h4 className="mb-3 font-semibold text-gray-900">Content Preview</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                    <span className="text-gray-700">
                      <span className="font-medium">{formData.content.length}</span>
                      {' '}
                      characters
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-600"></div>
                    <span className="text-gray-700">
                      <span className="font-medium">
                        {formData.content.split(/\s+/).filter(word => word.length > 0).length}
                      </span>
                      {' '}
                      words
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter className="border-t">
          <div className="flex w-full justify-between">
            <Button
              variant="bordered"
              onPress={resetModal}
              radius="none"
              className="border-gray-200"
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              color="primary"
              onPress={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!formData.name.trim() || !formData.content.trim()}
              radius="none"
            >
              {isSubmitting
                ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  )
                : (
                    'Create Knowledge Base'
                  )}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
